import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {
    useDeleteProductsMutation,
    useProductsInfiniteQuery,
} from './useProductsQuery';
import {useProductsSearch} from './useProductsSearch';
import {useProductsTableColumns} from './useProductsTableColumns';
import {ProductsFilters} from './useProductsFilter';
import {Product} from '../types';

/**
 * Default sort: by NAME, ascending.
 *
 * ⚠️ This is NOT the "newest first on a creation timestamp" default the
 * checklist prefers, and the reason is concrete: `listProducts` selects only
 * `id` and `name` (see services/queries.ts), so there is no creation timestamp
 * in the payload to sort on. §3a's own rule is that a nullable key "silently
 * degrades to insertion order when every row lacks it" — here EVERY row lacks
 * it, so defaulting to `created` would be sorting on nothing while claiming to
 * sort on time.
 *
 * `name` is what an operator scans a product catalogue by, and it is stable —
 * which is the property that actually matters, since the list must not
 * reshuffle under someone reading it. Nulls sort last, so unnamed products
 * collect at the bottom rather than at the top.
 *
 * Restoring the timestamp default means adding `created` to the GraphQL
 * selection set first. That is a query change, not a sort-config change.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<Product> = {
    columnAccessor: 'name',
    direction: 'asc',
};

/**
 * Which fields a keyword matches against — which is every field this API
 * returns. `id` is included because a Stripe product id (`prod_...`) is what an
 * operator pastes in from the Stripe dashboard.
 *
 * `description` is matched too even though the query does not currently select
 * it: the field is on the type, and matching an absent value is a harmless
 * no-op that starts working the day the selection set gains it.
 */
const matchesKeyword = (item: Product, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.name,
        item.description,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active filter. Filters are exact matches on
 * a stringified field; an empty value means the field is unset and is ignored.
 *
 * ⚠️ Nothing sets filters today — this module has no filter drawer, because
 * `listProducts` returns no field an operator could pick from. See
 * useProductsFilter.ts for why the seam is kept anyway.
 */
const matchesFilters = (item: Product, filters: ProductsFilters): boolean =>
    Object.entries(filters)
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof Product];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ SCOPE OF SORT AND FILTER: the pages FETCHED SO FAR.
 *
 * `listProducts($limit, $nextToken)` accepts NO filter and no sort argument —
 * not even the `filter` input its invoices and prices siblings have — so the
 * server can neither narrow nor order the result set. Everything below
 * therefore operates on the loaded window only. Sorting page 1 does not sort
 * the dataset, and a keyword matches only rows the browser has already
 * received; the other rows were never examined.
 *
 * This is a property of the token-pagination model against this API, not a bug
 * and not an oversight. It is also exactly why the footer states a LOADED count
 * and never a total: see DataTableTokenPagination, which is passed `filterTerm`
 * so a filtered view says "N of M loaded records match" rather than implying it
 * searched everything.
 *
 * Fixing it properly means a filter argument on the API — a backend change,
 * not a footer fix.
 */
export const useProducts = (filters: ProductsFilters = {}) => {
    const query = useProductsInfiniteQuery();
    const {columns} = useProductsTableColumns();
    const {keyword} = useProductsSearch();
    const deleteMutation = useDeleteProductsMutation();

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: Product[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean);

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare. Do not
    // hand-roll one; the null rule is what a hand-rolled version gets wrong.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<Product>(matched, DEFAULT_SORT_STATUS);

    // Sorted separately rather than taking `sorted` from the hook: `matched`
    // is rebuilt every render, and sortRecords is the same comparator applied
    // to the current array.
    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        deleteRecords: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                deleteMutation.mutate(ids);
            }
        },
        deleteMutation,
    };
};
